import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import supabase from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import TurnstileWidget from '@/components/shared/TurnstileWidget';
import { captchaOptions, isCaptchaPending } from '@/lib/turnstile';

// Sign-in form shown inside the sliding auth panel (see AuthSlider). `active` is false while the
// sign-up side is showing, so only one Turnstile widget is mounted at a time.
const LoginForm = ({ active = true, onSwitch }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState(''); // '', 'sending', 'sent', 'error'
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const refreshCaptcha = () => setCaptchaNonce((n) => n + 1);

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await signIn(email, password, captchaToken);

    if (error) {
      if (error.message === 'PENDING_APPROVAL') {
        navigate('/pending-approval', { replace: true });
        return;
      } else if (error.message?.toLowerCase().includes('email not confirmed')) {
        setError('Please verify your email address before signing in. Check your inbox for the confirmation link.');
      } else if (error.message?.toLowerCase().includes('invalid login credentials')) {
        setError('Incorrect email or password. Please check your credentials and try again.');
      } else {
        setError(error.message);
      }
      refreshCaptcha();
      setLoading(false);
      return;
    }

    navigate(from, { replace: true });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) return;

    setResetStatus('sending');
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      ...captchaOptions(captchaToken),
    });

    refreshCaptcha();
    setResetStatus(error ? 'error' : 'sent');
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setResetStatus('');
  };

  if (showForgotPassword) {
    return (
      <div className="w-full max-w-sm mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-1">Reset your password</h1>
          <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link</p>
        </div>

        {resetStatus === 'sent' ? (
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              If an account exists for <span className="font-medium text-foreground">{resetEmail}</span>,
              you'll receive a password reset link shortly.
            </p>
            <Button variant="outline" className="w-full" onClick={closeForgotPassword} data-testid="login-back-to-signin">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="reset-email">Email address</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </div>

            {active && <TurnstileWidget key={`reset-${captchaNonce}`} onToken={setCaptchaToken} />}

            {resetStatus === 'error' && (
              <p className="text-sm text-destructive">Something went wrong. Please try again.</p>
            )}

            <Button type="submit" className="w-full" disabled={resetStatus === 'sending' || !resetEmail || isCaptchaPending(captchaToken)}>
              {resetStatus === 'sending' ? 'Sending...' : 'Send reset link'}
            </Button>

            <Button variant="ghost" className="w-full" type="button" onClick={closeForgotPassword} data-testid="login-back-to-signin">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to sign in
            </Button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="mb-8">
        <img src="/logo-colour.png" alt="Digital Reporting - comply confidently" className="h-12 w-auto mb-6" />
        <h1 className="text-2xl font-bold mb-1">Sign in to your account</h1>
        <p className="text-sm text-muted-foreground">Secure Client Portal</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="login-email">Email address</Label>
          <Input
            id="login-email"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="login-password">Password</Label>
            <button
              type="button"
              className="text-xs text-primary underline cursor-pointer hover:text-primary/80"
              onClick={() => { setShowForgotPassword(true); setResetEmail(email); }}
              data-testid="login-forgot-password"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {active && <TurnstileWidget key={`login-${captchaNonce}`} onToken={setCaptchaToken} />}

        <Button type="submit" className="w-full" disabled={loading || !email || !password || isCaptchaPending(captchaToken)} data-testid="login-submit">
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <p className="text-sm text-muted-foreground mt-6 md:hidden">
        Don't have an account?{' '}
        <button type="button" onClick={onSwitch} className="font-medium text-primary hover:underline" data-testid="login-switch-mobile">
          Sign up
        </button>
      </p>
    </div>
  );
};

export default LoginForm;
